package com.trevorwiebe.timesheet.mileage.domain.model

import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.formatWithCommas

data class OdometerNeighbors(
    val previousByDate: MileageEntry? = null,
    val nextByDate: MileageEntry? = null,
    val lastBelowMileage: MileageEntry? = null,
    val firstAboveMileage: MileageEntry? = null,
)

sealed interface OdometerPlacement {
    data object Fits : OdometerPlacement

    data class LargeJump(val milesSincePrevious: Long) : OdometerPlacement

    data class Conflict(
        val conflicting: MileageEntry,
        val readingTooLow: Boolean,
        val fitsAfter: MileageEntry?,
        val fitsBefore: MileageEntry?,
    ) : OdometerPlacement
}

fun evaluateOdometerPlacement(mileageValue: Long, neighbors: OdometerNeighbors): OdometerPlacement {
    val previous = neighbors.previousByDate
    val next = neighbors.nextByDate
    if (previous != null && mileageValue < previous.mileageValue) {
        return OdometerPlacement.Conflict(
            conflicting = previous,
            readingTooLow = true,
            fitsAfter = neighbors.lastBelowMileage,
            fitsBefore = neighbors.firstAboveMileage,
        )
    }
    if (next != null && mileageValue > next.mileageValue) {
        return OdometerPlacement.Conflict(
            conflicting = next,
            readingTooLow = false,
            fitsAfter = neighbors.lastBelowMileage,
            fitsBefore = neighbors.firstAboveMileage,
        )
    }
    if (previous != null && mileageValue > previous.mileageValue + ODOMETER_WARNING_THRESHOLD) {
        return OdometerPlacement.LargeJump(mileageValue - previous.mileageValue)
    }
    return OdometerPlacement.Fits
}

private val CONNECTIVITY_MARKERS = listOf(
    "unavailable", "offline", "network", "timed out", "timeout",
    "unreachable", "failed to connect", "no address", "host",
)

fun odometerLookupErrorMessage(error: String?): String {
    val looksLikeConnectivity = error != null &&
        CONNECTIVITY_MARKERS.any { error.contains(it, ignoreCase = true) }
    return if (looksLikeConnectivity) {
        "Couldn't reach the server to check this truck's mileage history. " +
            "Check your connection and try again."
    } else {
        "Something went wrong checking this truck's mileage history. Please try again."
    }
}

fun odometerConflictMessage(mileageValue: Long, conflict: OdometerPlacement.Conflict): String {
    val entered = mileageValue.formatWithCommas()
    val conflictMiles = conflict.conflicting.mileageValue.formatWithCommas()
    val lead = if (conflict.readingTooLow) {
        "This truck was already at $conflictMiles mi on " +
            "${Util.toFriendlyDateTime(conflict.conflicting.date)}."
    } else {
        "This truck was only at $conflictMiles mi on " +
            "${Util.toFriendlyDateTime(conflict.conflicting.date)}."
    }
    val fitsAfter = conflict.fitsAfter
    val fitsBefore = conflict.fitsBefore
    val moveDate = when {
        fitsAfter != null && fitsBefore != null ->
            "set the date between ${Util.toFriendlyDateTime(fitsAfter.date)} and " +
                Util.toFriendlyDateTime(fitsBefore.date)
        fitsBefore != null -> "set the date before ${Util.toFriendlyDateTime(fitsBefore.date)}"
        fitsAfter != null -> "set the date after ${Util.toFriendlyDateTime(fitsAfter.date)}"
        else -> "correct the date"
    }
    val keepDate = if (conflict.readingTooLow) {
        "enter $conflictMiles or more"
    } else {
        "enter $conflictMiles or less"
    }
    return "$lead To save $entered mi, $moveDate — or $keepDate to keep this date."
}
